import { useState, useCallback } from 'react';

/**
 * Hook to manage nested dialogs with auto-selection after item creation
 * @param {Function} fetchList - Function to refresh the list after creation
 * @param {String} idField - Field name for the ID (default: 'id')
 * @returns {Object} Dialog state and handlers
 */
export const useNestedDialogWithAutoSelect = (fetchList, idField = 'id') => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectionCallback, setSelectionCallback] = useState(null);
  const [loading, setLoading] = useState(false);

  /**
   * Opens the dialog for creating a new item
   * @param {Function} onSelectCallback - Callback to execute with newly created item
   */
  const openDialog = useCallback((onSelectCallback) => {
    setSelectedItem(null); // Always null for "Create New"
    setSelectionCallback(() => onSelectCallback);
    setDialogOpen(true);
  }, []);

  /**
   * Closes the dialog without selection
   */
  const closeDialog = useCallback(async () => {
    setDialogOpen(false);
    setSelectedItem(null);
    
    // Refresh the list even if nothing was created
    if (fetchList) {
      await fetchList();
    }
    
    // Clear callback
    setSelectionCallback(null);
  }, [fetchList]);

  /**
   * Called when item is successfully created
   * @param {Object} newItem - The newly created item
   */
  const handleItemCreated = useCallback(async (newItem) => {
    setDialogOpen(false);
    
    // Wait before fetching to allow backend to commit the transaction
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Refresh the list
    if (fetchList) {
      await fetchList();
    }
    
    // Small delay to let React update state
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Execute the stored callback to auto-select AFTER list is refreshed
    if (selectionCallback && newItem) {
      // Additional delay to ensure React has fully re-rendered with new list
      setTimeout(() => {
        selectionCallback(newItem);
      }, 300);
    }
    
    setSelectionCallback(null);
    setSelectedItem(null);
  }, [selectionCallback, fetchList]);

  return {
    // State
    dialogOpen,
    selectedItem,
    loading,
    setLoading,
    
    // Actions
    openDialog,
    closeDialog,
    handleItemCreated,
    
    // Props to spread directly to dialog component
    dialogProps: {
      open: dialogOpen,
      handleClose: closeDialog,
      setLoading,
      selectedItem, // Will be null for "Create New"
    },
  };
};

